import { useState } from "react";
import { Lock, ShieldCheck, LogOut, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";

export default function ForcePasswordChange() {
  const { user, logout, refresh } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const inputCls =
    "w-full px-4 py-3 rounded-lg bg-[#050505] border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-electric focus:ring-1 focus:ring-electric/30 transition";

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated. Welcome to your dashboard.");
      await refresh(); // re-pulls /auth/me, clears must_change_password, unblocks the dashboard
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not update password."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-5 py-12"
      data-testid="force-password-change"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-electric/15 ring-1 ring-electric/40 flex items-center justify-center mx-auto mb-5">
            <ShieldCheck className="w-8 h-8 text-electric" />
          </div>
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-3">
            First-time Login
          </div>
          <h1 className="font-display font-black text-3xl tracking-tighter mb-2">
            Set your <span className="italic text-electric">password.</span>
          </h1>
          <p className="text-sm text-zinc-400">
            Signed in as <strong className="text-zinc-200">{user?.email}</strong>. Choose a new
            password to access the admin dashboard.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="space-y-5 p-7 rounded-2xl bg-[#0a0a0a] border border-white/5"
        >
          <Field label="Current (temporary) password" icon={<Lock className="w-3.5 h-3.5" />}>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className={inputCls}
              data-testid="fpc-current"
            />
          </Field>
          <Field label="New password" icon={<Lock className="w-3.5 h-3.5" />}>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className={inputCls}
              placeholder="At least 8 characters"
              data-testid="fpc-new"
            />
          </Field>
          <Field label="Confirm new password" icon={<Lock className="w-3.5 h-3.5" />}>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={inputCls}
              data-testid="fpc-confirm"
            />
          </Field>

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-electric text-black px-6 py-3.5 rounded-full font-bold tracking-wide hover:bg-[#4ade80] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60 disabled:hover:translate-y-0"
            data-testid="fpc-submit"
          >
            <CheckCircle2 className="w-4 h-4" />
            {submitting ? "Updating..." : "Update Password & Continue"}
          </button>
        </form>

        <button
          onClick={logout}
          className="w-full mt-5 inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-electric transition"
          data-testid="fpc-logout"
        >
          <LogOut className="w-3 h-3" /> Sign out
        </button>
      </div>
    </main>
  );
}

function Field({ label, icon, children }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-2">
        {icon}
        {label}
      </label>
      {children}
    </div>
  );
}
